package ai.jessie.web.settings

import ai.jessie.shared.settings.OrganisationSettingsResponse
import ai.jessie.shared.settings.SaveOrganisationSettingsRequest
import ai.jessie.shared.settings.SettingsPreviewResponse
import ai.jessie.shared.settings.SettingsProblemResponse
import ai.jessie.web.onboarding.apiRequest
import ai.jessie.web.onboarding.decodeProblem
import ai.jessie.web.onboarding.validOrganisationId
import androidx.compose.runtime.staticCompositionLocalOf
import kotlinx.serialization.json.Json

interface SettingsPort {
    suspend fun read(organisationId: String): OrganisationSettingsResponse

    suspend fun preview(
        organisationId: String,
        request: SaveOrganisationSettingsRequest,
    ): SettingsPreviewResponse

    suspend fun save(
        organisationId: String,
        request: SaveOrganisationSettingsRequest,
    ): OrganisationSettingsResponse
}

val LocalSettingsPort =
    staticCompositionLocalOf<SettingsPort> {
        error("Settings port must be supplied by the application")
    }

class SettingsRequestFailure(val status: Int, val detail: SettingsProblemResponse) :
    Exception("Settings request failed")

class BrowserSettingsPort : SettingsPort {
    private val json = Json { ignoreUnknownKeys = true }

    override suspend fun read(organisationId: String) =
        json.decodeFromString<OrganisationSettingsResponse>(request(organisationId))

    override suspend fun preview(organisationId: String, request: SaveOrganisationSettingsRequest) =
        json.decodeFromString<SettingsPreviewResponse>(
            request(organisationId, "/validate", request)
        )

    override suspend fun save(organisationId: String, request: SaveOrganisationSettingsRequest) =
        json.decodeFromString<OrganisationSettingsResponse>(request(organisationId, body = request))

    private suspend fun request(
        organisationId: String,
        suffix: String = "",
        body: SaveOrganisationSettingsRequest? = null,
    ): String {
        require(validOrganisationId(organisationId))
        return apiRequest(
            json,
            "/organisations/$organisationId/settings$suffix",
            body?.let { json.encodeToString(it) },
        ) { status, text ->
            val detail = json.decodeProblem(text, SettingsProblemResponse(""))
            SettingsRequestFailure(status, detail)
        }
    }
}
